import { database, auth } from '../services/firebase';
import { ref, get, push, set } from 'firebase/database';
import { signInAnonymously } from 'firebase/auth';

const NOTIFICATIONS_PATH = 'notifications';
const READ_NOTIFICATIONS_PATH = 'notificationReads';

// Get all notifications from Firebase
export const getAllNotifications = async () => {
  try {
    const snapshot = await get(ref(database, NOTIFICATIONS_PATH));
    if (snapshot.exists()) {
      const data = snapshot.val();
      return Object.entries(data).map(([id, notification]) => ({
        ...notification,
        id,
      }));
    }
    return [];
  } catch (error) {
    console.error('Error loading notifications from Firebase:', error);
    return [];
  }
};

// Get notifications for a specific user
export const getNotificationsForUser = async (username) => {
  if (!username) return [];
  try {
    const all = await getAllNotifications();
    return all
      .filter((notification) => {
        if (notification.targetType === 'all') return true;
        if (notification.targetType === 'users' && Array.isArray(notification.recipients)) {
          return notification.recipients.includes(username);
        }
        return false;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch (error) {
    console.error('Error filtering notifications:', error);
    return [];
  }
};

// Add a new notification to Firebase
export const addNotification = async ({ subject, message, targetType = 'all', recipients = [], createdBy }) => {
  try {
    // Ensure we have an authenticated Firebase session before writing
    if (!auth.currentUser) {
      try {
        await signInAnonymously(auth);
        console.log('Signed in anonymously to Firebase before adding notification');
      } catch (err) {
        console.warn('Anonymous sign-in failed before addNotification:', err);
      }
    }
    const notification = {
      subject: subject || 'New notification',
      message: message || '',
      targetType,
      recipients: Array.isArray(recipients) ? recipients : [],
      createdBy: createdBy || 'admin',
      createdAt: new Date().toISOString(),
    };

    try {
      const newNotificationRef = await push(ref(database, NOTIFICATIONS_PATH), notification);
      return { id: newNotificationRef.key, ...notification };
    } catch (err) {
      // If permissions denied, attempt one anonymous sign-in and retry once
      if (err?.code === 'PERMISSION_DENIED' || /permission/i.test(String(err))) {
        try {
          await signInAnonymously(auth);
          const retryRef = await push(ref(database, NOTIFICATIONS_PATH), notification);
          return { id: retryRef.key, ...notification };
        } catch (retryErr) {
          console.error('Retry after anonymous sign-in failed:', retryErr);
          throw retryErr;
        }
      }
      throw err;
    }
  } catch (error) {
    console.error('Error adding notification to Firebase:', error);
    throw error;
  }
};

// Get read notification IDs for a user
export const getReadNotificationsForUser = async (username) => {
  if (!username) return [];
  try {
    const snapshot = await get(ref(database, `${READ_NOTIFICATIONS_PATH}/${username}`));
    if (snapshot.exists()) {
      const data = snapshot.val();
      return Array.isArray(data) ? data : Object.values(data || {});
    }
    return [];
  } catch (error) {
    console.error('Error loading read notifications:', error);
    return [];
  }
};

// Mark a notification as read for a user
export const markNotificationRead = async (username, notificationId) => {
  if (!username || !notificationId) return;
  try {
    if (!auth.currentUser) {
      try { await signInAnonymously(auth); } catch (e) { console.warn('Anonymous sign-in failed in markNotificationRead', e); }
    }
    const current = await getReadNotificationsForUser(username);
    if (!current.includes(notificationId)) {
      const updated = [...current, notificationId];
      await set(ref(database, `${READ_NOTIFICATIONS_PATH}/${username}`), updated);
    }
  } catch (error) {
    console.error('Error marking notification as read:', error);
  }
};

// Get unread count for a user
export const getUnreadCountForUser = async (username) => {
  if (!username) return 0;
  try {
    const all = await getNotificationsForUser(username);
    const readIds = await getReadNotificationsForUser(username);
    return all.filter((notification) => !readIds.includes(notification.id)).length;
  } catch (error) {
    console.error('Error calculating unread count:', error);
    return 0;
  }
};

